import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { tours, COMPANY_TOUR_KEYS } from '@/help/content/tours'

const STORAGE_BASE = 'fe_gestor_tours_v1'
const NEW_COMPANY_TTL_MS = 30 * 24 * 60 * 60 * 1000

const emptyPrefs = () => ({ disabled: false, done: {}, newCompanies: {} })

export const useTourStore = defineStore('tour', () => {
  const authStore = useAuthStore()

  const prefs = ref(emptyPrefs())

  const activeKey = ref(null)
  const stepIndex = ref(0)
  const scope = ref(null)
  const autoStarted = ref(false)
  const direction = ref(1)

  const tour = computed(() => (activeKey.value ? tours[activeKey.value] ?? null : null))
  const steps = computed(() => tour.value?.steps ?? [])
  const step = computed(() => steps.value[stepIndex.value] ?? null)
  const isActive = computed(() => !!tour.value)
  const isLast = computed(() => stepIndex.value >= steps.value.length - 1)
  const isDisabled = computed(() => prefs.value.disabled === true)

  function storageKey() {
    return `${STORAGE_BASE}_${authStore.user?.id ?? 'anon'}`
  }

  function load() {
    try {
      const raw = localStorage.getItem(storageKey())
      const parsed = raw ? JSON.parse(raw) : null
      prefs.value = {
        ...emptyPrefs(),
        ...(parsed && typeof parsed === 'object' ? parsed : {})
      }
    } catch {
      prefs.value = emptyPrefs()
    }
  }

  function persist() {
    try {
      localStorage.setItem(storageKey(), JSON.stringify(prefs.value))
    } catch {
    }
  }

  watch(() => authStore.user?.id, load, { immediate: true })

  function isDone(key, scopeId) {
    if (!scopeId) return false
    return (prefs.value.done[scopeId] ?? []).includes(key)
  }

  function markDone(key, scopeId) {
    if (!key || !scopeId || isDone(key, scopeId)) return
    const done = { ...prefs.value.done, [scopeId]: [...(prefs.value.done[scopeId] ?? []), key] }
    const newCompanies = { ...prefs.value.newCompanies }
    if (scopeId !== 'user' && COMPANY_TOUR_KEYS.every((k) => done[scopeId].includes(k))) {
      delete newCompanies[scopeId]
    }
    prefs.value = { ...prefs.value, done, newCompanies }
    persist()
  }

  function markCompanyNew(companyId) {
    if (companyId == null || companyId === '') return
    const id = String(companyId)
    prefs.value = {
      ...prefs.value,
      newCompanies: { ...prefs.value.newCompanies, [id]: Date.now() }
    }
    persist()
  }

  function isCompanyNew(companyId) {
    if (companyId == null || companyId === '') return false
    const id = String(companyId)
    const since = Number(prefs.value.newCompanies[id]) || 0
    if (!since) return false
    if (Date.now() - since > NEW_COMPANY_TTL_MS) {
      const newCompanies = { ...prefs.value.newCompanies }
      delete newCompanies[id]
      prefs.value = { ...prefs.value, newCompanies }
      persist()
      return false
    }
    return true
  }

  function disableAll() {
    prefs.value = { ...prefs.value, disabled: true }
    persist()
  }

  function start(key, { scope: scopeId = null, auto = false } = {}) {
    if (!tours[key]) return false
    activeKey.value = key
    stepIndex.value = 0
    scope.value = scopeId
    autoStarted.value = auto
    direction.value = 1
    return true
  }

  function next() {
    direction.value = 1
    if (isLast.value) {
      end('completed')
      return
    }
    stepIndex.value += 1
  }

  function prev() {
    if (stepIndex.value === 0) return
    direction.value = -1
    stepIndex.value -= 1
  }

  function skipCurrent() {
    if (direction.value < 0 && stepIndex.value > 0) {
      stepIndex.value -= 1
      return
    }
    direction.value = 1
    next()
  }

  function end(reason = 'skipped') {
    if (!activeKey.value) return
    markDone(activeKey.value, scope.value)
    activeKey.value = null
    stepIndex.value = 0
    scope.value = null
    autoStarted.value = false
    return reason
  }

  return {
    activeKey,
    stepIndex,
    scope,
    autoStarted,
    tour,
    steps,
    step,
    isActive,
    isLast,
    isDisabled,
    isDone,
    markDone,
    markCompanyNew,
    isCompanyNew,
    disableAll,
    start,
    next,
    prev,
    skipCurrent,
    end
  }
})
